import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Filter, X } from 'lucide-react';
import Fuse from 'fuse.js';
import { debounce } from 'lodash-es';

interface SearchableItem {
  id: string;
  [key: string]: any;
}

interface FilterOption {
  field: string;
  label: string;
  options: { value: string; label: string }[];
}

interface SearchAndFilterProps<T extends SearchableItem> {
  items: T[];
  searchKeys: string[];
  filterOptions: FilterOption[];
  onResultsChange: (results: T[]) => void;
  placeholder?: string;
}

const SearchAndFilter = <T extends SearchableItem>({
  items,
  searchKeys,
  filterOptions,
  onResultsChange,
  placeholder = 'Search...',
}: SearchAndFilterProps<T>) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Memoize Fuse instance
  const fuse = useMemo(() => {
    try {
      return new Fuse(items, {
        keys: searchKeys.map(key => ({
          name: key,
          weight: 1
        })),
        threshold: 0.3,
        ignoreLocation: true,
        useExtendedSearch: true,
        getFn: (obj, path) => {
          if (typeof path === 'string') {
            const value = obj[path];
            return value?.toString() || '';
          }
          return '';
        }
      });
    } catch (error) {
      console.error('Error creating Fuse instance:', error);
      return null;
    }
  }, [items, searchKeys]);

  // Memoize search function
  const performSearch = useCallback(
    debounce((query: string, filters: Record<string, string>) => {
      try {
        // First apply search if Fuse instance exists
        let results = query && fuse
          ? fuse.search(query).map(result => result.item)
          : items;

        // Then apply filters
        results = results.filter(item =>
          Object.entries(filters).every(([field, value]) => {
            if (!value) return true;
            return item[field] === value;
          })
        );

        onResultsChange(results);
      } catch (error) {
        console.error('Error performing search:', error);
        onResultsChange(items); // Fallback to showing all items
      }
    }, 300),
    [items, fuse, onResultsChange]
  );

  // Effect for search and filter
  useEffect(() => {
    performSearch(searchQuery, activeFilters);
    return () => performSearch.cancel();
  }, [searchQuery, activeFilters, performSearch]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery('');
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 
              bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 
              focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="px-4 py-2 flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 
            bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 
            hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Filter className="w-5 h-5" />
          Filters
          {Object.values(activeFilters).some(Boolean) && (
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
              {Object.values(activeFilters).filter(Boolean).length}
            </span>
          )}
        </button>

        {(searchQuery || Object.values(activeFilters).some(Boolean)) && (
          <button
            onClick={clearFilters}
            className="px-4 py-2 flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 
              bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 
              hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
            Clear
          </button>
        )}
      </div>

      {isFilterOpen && (
        <div className="p-4 rounded-lg border border-gray-300 dark:border-gray-700 
          bg-white dark:bg-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterOptions.map((filter) => (
              <div key={filter.field}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {filter.label}
                </label>
                <select
                  value={activeFilters[filter.field] || ''}
                  onChange={(e) => handleFilterChange(filter.field, e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 
                    bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 
                    py-2 px-3"
                >
                  <option value="">All</option>
                  {filter.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(SearchAndFilter);